import { useCallback, useEffect, useMemo, useState } from "react";
import { useAPI } from "../../Context/MyContext";
import { toast } from "react-hot-toast";
import WorkflowCanvas from "./WorkflowCanvas";
import CreateWorkflowModal from "./Ui/CreateWorkflowModal";
import DeleteConfirmationModal from "./Ui/DeleteConfirmationModal";
import {
  ChevronLeft, ChevronRight, Circle, Loader2, Maximize2, Minimize2, MoreVertical, Plus, RefreshCw, Search, Settings2, Trash2
} from "lucide-react";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
}

export default function WorkflowUi() {
  const { getWorkflows, deleteWorkflow, toggleWorkflowStatus, createWorkflow, getPhoneAll } = useAPI();

  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [phones, setPhones] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState("");

  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [menuWorkflowId, setMenuWorkflowId] = useState(null);
  const [loadingWorkflowId, setLoadingWorkflowId] = useState(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createStatus, setCreateStatus] = useState("idle");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState("idle");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditorFocus, setIsEditorFocus] = useState(false);

  const loadWorkflows = useCallback(
    async (preferredWorkflowId = null) => {
      try {
        setLoading(true);
        const res = await getWorkflows();
        const data = res?.data || [];
        setWorkflows(data);

        const keepId = preferredWorkflowId || selectedWorkflow?._id;
        const nextSelected = data.find((wf) => wf._id === keepId) || data[0] || null;
        setSelectedWorkflow(nextSelected);
      } catch (err) {
        console.error("Load workflows error:", err);
      } finally {
        setLoading(false);
      }
    },
    [getWorkflows, selectedWorkflow?._id]
  );

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getPhoneAll();
        if (res?.success) {
          setPhones(res.data || []);
        }
      } catch (err) {
        console.error("Load phones error:", err);
      }
    })();
  }, [getPhoneAll]);

  useEffect(() => {
    setSelectedPhone(selectedWorkflow?.phone_number_id || "");
  }, [selectedWorkflow?._id, selectedWorkflow?.phone_number_id]);

  useEffect(() => {
    const closeMenus = () => setMenuWorkflowId(null);
    window.addEventListener("click", closeMenus);
    return () => window.removeEventListener("click", closeMenus);
  }, []);

  // console.log("first selectedWorkflow ::", selectedWorkflow)

  const filteredWorkflows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return workflows.filter((wf) => {
      const matchesSearch =
        !q ||
        wf?.name?.toLowerCase().includes(q) ||
        wf?.description?.toLowerCase().includes(q) ||
        wf?._id?.toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && wf.isActive) ||
        (statusFilter === "inactive" && !wf.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [workflows, search, statusFilter]);

  const stats = useMemo(() => {
    const active = workflows.filter((w) => w.isActive).length;
    return {
      total: workflows.length,
      active,
      inactive: workflows.length - active
    };
  }, [workflows]);

  const openCreateWorkflow = () => {
    setIsCreateOpen(true);
    setMenuWorkflowId(null);
  };

  const handleCreateWorkflow = async (payload) => {
    try {
      setCreateStatus("loading");
      const res = await createWorkflow(payload);

      if (res?.success) {
        setCreateStatus("success");
        await loadWorkflows();
        setTimeout(() => {
          setIsCreateOpen(false);
          setCreateStatus("idle");
        }, 800);
      } else {
        setCreateStatus("error");
      }
    } catch (error) {
      console.error("Create workflow error:", error);
      setCreateStatus("error");
    }
  };

  const promptDeleteWorkflow = (workflow) => {
    setMenuWorkflowId(null);
    setWorkflowToDelete(workflow);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!workflowToDelete) return;

    try {
      setDeleteLoading(true);
      setDeleteStatus("idle");

      const res = await deleteWorkflow(workflowToDelete._id);
      if (res?.success) {
        setDeleteStatus("success");
        await loadWorkflows();
        setTimeout(() => {
          setDeleteModalOpen(false);
          setDeleteStatus("idle");
          setWorkflowToDelete(null);
        }, 700);
      } else {
        setDeleteStatus("error");
      }
    } catch (err) {
      console.error("Delete workflow error:", err);
      setDeleteStatus("error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleActive = async (workflow, phoneNumberIdOverride = null) => {
    const phoneNumberId = phoneNumberIdOverride || workflow?.phone_number_id;
    if (!phoneNumberId) {
      toast.error("Please select a WhatsApp Business number first.");
      return;
    }

    try {
      setLoadingWorkflowId(workflow._id);

      await toggleWorkflowStatus({
        workflowId: workflow._id,
        isActive: !workflow.isActive,
        phone_number_id: phoneNumberId
      });

      await loadWorkflows(workflow._id);
    } catch (err) {
      console.error("Failed to toggle workflow status", err);
      toast.error(err?.response?.data?.message || "Failed to update workflow status.");
    } finally {
      setLoadingWorkflowId(null);
      setMenuWorkflowId(null);
    }
  };

  //  console.log("first selectedWorkflow ::", selectedWorkflow)

  return (
    <div className={`h-screen bg-slate-100 ${isEditorFocus ? "p-0 overflow-hidden" : "p-4 overflow-y-auto"}`}>
      <div className="h-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="h-full flex relative">
          <aside
            className={`h-full border-r border-slate-200 bg-white transition-all duration-300 ease-in-out ${isEditorFocus ? "w-0" : isPanelOpen ? "w-[340px]" : "w-0"
              }`}
          >
            <div className={`h-full flex flex-col ${isPanelOpen && !isEditorFocus ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">Workflows</h2>
                    <p className="text-xs text-slate-500">Build and manage your automation flows</p>
                  </div>
                  <button
                    className="p-1.5 rounded-lg hover:bg-slate-100"
                    onClick={() => setIsPanelOpen(false)}
                    title="Collapse"
                  >
                    <ChevronLeft size={16} />
                  </button>
                </div>

                <div className="mt-3 relative">
                  <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search workflows"
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  />
                </div>

                <div className="mt-3 flex gap-2">
                  {["all", "active", "inactive"].map((status) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1.5 text-xs rounded-full border transition ${statusFilter === status
                        ? "bg-green-50 border-green-200 text-green-700"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                      {status[0].toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-slate-50 border border-slate-200 py-2">
                    <p className="text-[11px] text-slate-500">Total</p>
                    <p className="text-sm font-semibold text-slate-900">{stats.total}</p>
                  </div>
                  <div className="rounded-lg bg-green-50 border border-green-200 py-2">
                    <p className="text-[11px] text-green-700">Active</p>
                    <p className="text-sm font-semibold text-green-700">{stats.active}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 py-2">
                    <p className="text-[11px] text-slate-500">Inactive</p>
                    <p className="text-sm font-semibold text-slate-900">{stats.inactive}</p>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3 bg-slate-50">
                {loading ? (
                  <div className="h-full flex items-center justify-center text-slate-500 text-sm">
                    <Loader2 size={16} className="animate-spin mr-2" /> Loading workflows...
                  </div>
                ) : filteredWorkflows.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-center px-4">
                    <div>
                      <p className="text-sm font-medium text-slate-700">No workflows found</p>
                      <p className="text-xs text-slate-500 mt-1">Try another search/filter or create a new one.</p>
                    </div>
                  </div>
                ) : (
                  filteredWorkflows.map((wf) => {
                    const isSelected = selectedWorkflow?._id === wf._id;
                    return (
                      <div
                        key={wf._id}
                        onClick={() => setSelectedWorkflow(wf)}
                        className={`mb-2 p-3 rounded-xl border cursor-pointer transition ${isSelected
                          ? "bg-white border-green-300 ring-1 ring-green-200"
                          : "bg-white border-slate-200 hover:border-slate-300"
                          }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <Circle
                                size={10}
                                className={wf.isActive ? "text-green-500 fill-green-500" : "text-slate-400 fill-slate-400"}
                              />
                              <h3 className="text-sm font-semibold text-slate-900 truncate">{wf.name || "Untitled workflow"}</h3>
                            </div>
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{wf.description || "No description"}</p>
                            <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                              <span className="px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
                                {wf.triggerType || "webhook_event"}
                              </span>
                              <span>Updated {formatDate(wf.updatedAt || wf.createdAt)}</span>
                            </div>
                          </div>

                          <div className="relative shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuWorkflowId((prev) => (prev === wf._id ? null : wf._id));
                              }}
                              className="p-1.5 rounded-md hover:bg-slate-100"
                            >
                              <MoreVertical size={16} className="text-slate-500" />
                            </button>

                            {menuWorkflowId === wf._id && (
                              <div
                                className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg z-30"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                                  onClick={() => {
                                    setSelectedWorkflow(wf);
                                    setMenuWorkflowId(null);
                                  }}
                                >
                                  Open
                                </button>

                                <button
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
                                  onClick={() => handleToggleActive(wf)}
                                  disabled={loadingWorkflowId === wf._id}
                                >
                                  {loadingWorkflowId === wf._id
                                    ? "Updating..."
                                    : wf.isActive
                                      ? "Deactivate"
                                      : "Activate"}
                                </button>

                                <button
                                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                  onClick={() => promptDeleteWorkflow(wf)}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="p-3 border-t border-slate-200 bg-white">
                <button
                  onClick={openCreateWorkflow}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2.5"
                >
                  <Plus size={16} /> New Workflow
                </button>
              </div>
            </div>
          </aside>

          <section className="flex-1 h-full bg-slate-100">
            <div className="h-full flex flex-col">
              {!isEditorFocus ? (
                <div className="h-14 px-4 border-b border-slate-200 bg-white flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    {!isPanelOpen && (
                      <button
                        onClick={() => setIsPanelOpen(true)}
                        className="p-1.5 rounded-lg hover:bg-slate-100"
                        title="Expand list"
                      >
                        <ChevronRight size={16} />
                      </button>
                    )}
                    <Settings2 size={16} className="text-slate-500" />
                    <p className="text-sm text-slate-700 truncate">
                      {selectedWorkflow ? selectedWorkflow.name : "Workflow Builder"}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => loadWorkflows()}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      <RefreshCw size={13} /> Refresh
                    </button>
                    <button
                      onClick={() => setIsEditorFocus(true)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                      title="Focus edit mode"
                    >
                      <Maximize2 size={13} /> Focus Edit
                    </button>

                    {selectedWorkflow ? (
                      <select
                        value={selectedPhone}
                        onChange={(e) => {
                          const phoneId = e.target.value;
                          setSelectedPhone(phoneId);
                          setSelectedWorkflow((prev) => (prev ? { ...prev, phone_number_id: phoneId } : prev));
                          setWorkflows((prev) =>
                            prev.map((wf) =>
                              wf._id === selectedWorkflow._id ? { ...wf, phone_number_id: phoneId } : wf
                            )
                          );
                        }}
                        className="w-56 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700"
                      >
                        <option value="">Select WhatsApp Business</option>
                        {phones.map((p) => (
                          <option key={p.phone_number_id} value={p.phone_number_id}>
                            {p.verified_name} ({p.waba_id})
                          </option>
                        ))}
                      </select>
                    ) : null}

                    {selectedWorkflow ? (
                      <button
                        onClick={() => handleToggleActive(selectedWorkflow, selectedPhone)}
                        disabled={loadingWorkflowId === selectedWorkflow._id}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border ${selectedWorkflow.isActive
                          ? "border-green-300 text-green-700 bg-green-50"
                          : "border-slate-300 text-slate-700 bg-white"
                          }`}
                      >
                        {loadingWorkflowId === selectedWorkflow._id ? "Updating..." : selectedWorkflow.isActive ? "Active" : "Inactive"}
                      </button>
                    ) : null}

                    {selectedWorkflow ? (
                      <button
                        onClick={() => promptDeleteWorkflow(selectedWorkflow)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-red-200 text-red-600 bg-red-50 hover:bg-red-100"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="h-12 px-4 border-b border-slate-200 bg-white flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700 truncate">{selectedWorkflow?.name || "Workflow Builder"}</p>
                  <button
                    onClick={() => setIsEditorFocus(false)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                    title="Exit focus mode"
                  >
                    <Minimize2 size={13} /> Exit Focus
                  </button>
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-auto">
                <WorkflowCanvas
                  workflow={selectedWorkflow}
                  hasWorkflows={workflows.length > 0}
                  onCreateWorkflow={openCreateWorkflow}
                  focusMode={isEditorFocus}
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      <CreateWorkflowModal
        isOpen={isCreateOpen}
        onClose={() => {
          if (createStatus !== "loading") {
            setIsCreateOpen(false);
            setCreateStatus("idle");
          }
        }}
        onCreate={handleCreateWorkflow}
        status={createStatus}
      />

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteModalOpen(false);
            setDeleteStatus("idle");
            setWorkflowToDelete(null);
          }
        }}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        status={deleteStatus}
        workflowName={workflowToDelete?.name}
      />
    </div>
  );
}
